"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useAppPreferences } from "@/components/AccentColorProvider";
import {
  extractRiskWorkspaceArtifacts,
  type RiskWorkspaceArtifact,
} from "@/components/risk-assessment/useRiskAssessmentRunController";
import { getDeviceContextSnapshot } from "@/lib/deviceContext";
import {
  buildAgentRunPayload,
  cancelAgentRun,
  createAgentRun,
  createAgentSession,
  loadAgentSession,
  loadAgentSessionMessages,
  openAgentRunEvents,
  type AgentFocusTarget,
  type AgentModelMode,
  type AgentSessionMessage,
} from "@/lib/agentRunTransport";
import { getAgentSessionHref, isPublicSessionId } from "@/lib/agentHub";
import type { ObjectRiskFocusDraft } from "@/lib/objectRiskFocus";
import {
  uploadObjectRiskPair,
  type ObjectRiskUploadProgress,
} from "@/lib/objectRiskUpload";
import {
  focusTargetFromMessages,
  latestGeneratingObjectRiskRun,
} from "./objectRiskContract";

export type ObjectRiskRunState =
  | "idle"
  | "loading"
  | "uploading"
  | "generating"
  | "ready"
  | "failed"
  | "cancelled";

export type ObjectRiskRunStage = {
  id: "prepare" | "observe" | "hazards" | "report";
  label: string;
  status: "pending" | "running" | "complete" | "error";
};

const INITIAL_STAGES: ObjectRiskRunStage[] = [
  { id: "prepare", label: "입력 준비", status: "pending" },
  { id: "observe", label: "선택 객체·현장 분석", status: "pending" },
  { id: "hazards", label: "객체 연계 위험 탐색·검증", status: "pending" },
  { id: "report", label: "위험성평가 보고서 생성", status: "pending" },
];

const stageIndexForEvent = (event: Record<string, unknown>) => {
  const id = String(event.agentId ?? "").toLowerCase();
  if (!id) return -1;
  if (id.includes("normalize")) return 0;
  if (id.includes("photo") || id.includes("classifier")) return 1;
  if (
    id.includes("search") ||
    id.includes("contextual") ||
    id.includes("scope")
  ) {
    return 2;
  }
  if (
    id.includes("draft") ||
    id.includes("verify") ||
    id.includes("evidence") ||
    id.includes("legal") ||
    id.includes("formatter") ||
    id === "riskagent"
  ) {
    return 3;
  }
  return -1;
};

const sleep = (milliseconds: number) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export function useObjectRiskAssessmentRun() {
  const searchParams = useSearchParams();
  const routeSessionId = searchParams.get("session");
  const { reasoningStyle } = useAppPreferences();

  const [state, setState] = useState<ObjectRiskRunState>(
    routeSessionId ? "loading" : "idle",
  );
  const [sessionId, setSessionId] = useState<string | null>(
    isPublicSessionId(routeSessionId) ? routeSessionId : null,
  );
  const [runId, setRunId] = useState<string | null>(null);
  const [focusTarget, setFocusTarget] = useState<AgentFocusTarget | null>(null);
  const [artifact, setArtifact] = useState<RiskWorkspaceArtifact | null>(null);
  const [streamedContent, setStreamedContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] =
    useState<ObjectRiskUploadProgress | null>(null);
  const [stages, setStages] = useState<ObjectRiskRunStage[]>(INITIAL_STAGES);

  const activeSessionIdRef = useRef<string | null>(sessionId);
  const streamAbortRef = useRef<AbortController | null>(null);
  const lastSequenceRef = useRef(0);
  const contentRef = useRef("");

  const progressPercent = useMemo(() => {
    if (state === "uploading") return uploadProgress?.percent ?? 0;
    if (state === "ready") return 100;
    const complete = stages.filter((stage) => stage.status === "complete").length;
    const running = stages.some((stage) => stage.status === "running") ? 0.45 : 0;
    return Math.round(((complete + running) / stages.length) * 100);
  }, [stages, state, uploadProgress]);

  const updateStage = useCallback(
    (index: number, status: ObjectRiskRunStage["status"]) => {
      if (index < 0 || index >= INITIAL_STAGES.length) return;
      setStages((previous) =>
        previous.map((stage, stageIndex) => {
          if (stageIndex < index && stage.status !== "complete") {
            return { ...stage, status: "complete" };
          }
          if (stageIndex !== index) return stage;
          if (stage.status === "complete" && status === "running") return stage;
          return { ...stage, status };
        }),
      );
    },
    [],
  );

  const loadCanonicalResult = useCallback(
    async (targetSessionId: string): Promise<RiskWorkspaceArtifact | null> => {
      let lastMessages: AgentSessionMessage[] = [];
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const messages = await loadAgentSessionMessages(targetSessionId);
        lastMessages = messages;
        if (activeSessionIdRef.current !== targetSessionId) return null;
        const nextFocus = focusTargetFromMessages(messages);
        if (nextFocus) setFocusTarget(nextFocus);
        const risk = [...extractRiskWorkspaceArtifacts(messages)]
          .reverse()
          .find((item) => item.type === "risk_assessment");
        if (risk) {
          setArtifact(risk);
          const assistant = [...messages]
            .reverse()
            .find(
              (message) =>
                message.role === "assistant" &&
                /<risk_assessment(?:\s|>)/i.test(message.content),
            );
          if (assistant) {
            contentRef.current = assistant.content;
            setStreamedContent(assistant.content);
          }
          return risk;
        }
        if (attempt < 5) await sleep(250 * (attempt + 1));
      }

      const lastAssistant = [...lastMessages]
        .reverse()
        .find((message) => message.role === "assistant");
      if (lastAssistant && /<ask_user(?:\s|>)/i.test(lastAssistant.content)) {
        throw new Error("선택 객체의 위험성을 평가하려면 추가 작업 정보가 필요합니다.");
      }
      return null;
    },
    [],
  );

  const finishRun = useCallback(
    async (targetSessionId: string) => {
      const result = await loadCanonicalResult(targetSessionId);
      if (activeSessionIdRef.current !== targetSessionId) return;
      if (!result) {
        setState("failed");
        setError("완성된 객체 중심 위험성평가 보고서를 확인하지 못했습니다.");
        updateStage(3, "error");
        return;
      }
      setStages((previous) =>
        previous.map((stage) => ({ ...stage, status: "complete" })),
      );
      setState("ready");
      setError(null);
      window.dispatchEvent(new Event("session-updated"));
    },
    [loadCanonicalResult, updateStage],
  );

  const applyEvent = useCallback(
    (event: Record<string, unknown>) => {
      const sequence = Number(event.sequence);
      if (Number.isSafeInteger(sequence)) {
        if (sequence <= lastSequenceRef.current) {
          return { terminal: false, type: "" };
        }
        lastSequenceRef.current = sequence;
      }
      const type = String(event.type ?? "");

      if (type === "text-snapshot" && typeof event.text === "string") {
        contentRef.current = event.text;
        setStreamedContent(event.text);
      }
      if (type === "agent-text-reset" && event.scope === "assistant") {
        contentRef.current = "";
        setStreamedContent("");
      }
      if (type === "run-completed" && typeof event.content === "string") {
        contentRef.current = event.content;
        setStreamedContent(event.content);
      }

      const stageIndex = stageIndexForEvent(event);
      if (
        (type === "agent-start" || type === "routing-decision") &&
        stageIndex >= 0
      ) {
        updateStage(stageIndex, "running");
      } else if (type === "agent-complete" && stageIndex >= 0) {
        updateStage(stageIndex, "complete");
      } else if (type === "error" && stageIndex >= 0) {
        updateStage(stageIndex, "error");
      }

      return {
        terminal: ["run-completed", "run-failed", "run-cancelled"].includes(
          type,
        ),
        type,
      };
    },
    [updateStage],
  );

  const consumeRun = useCallback(
    async (targetRunId: string, targetSessionId: string, after = 0) => {
      streamAbortRef.current?.abort();
      const controller = new AbortController();
      streamAbortRef.current = controller;
      lastSequenceRef.current = after;
      let terminal = false;

      while (!controller.signal.aborted && !terminal) {
        try {
          const response = await openAgentRunEvents(
            targetRunId,
            lastSequenceRef.current,
            controller.signal,
          );
          if (!response.ok || !response.body) {
            throw new Error(`이벤트 연결 실패 (${response.status})`);
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (!controller.signal.aborted) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const raw = line.slice(6).trim();
              if (!raw || raw === "[DONE]") continue;
              const event = JSON.parse(raw) as Record<string, unknown>;
              const result = applyEvent(event);
              if (!result.terminal) continue;
              terminal = true;

              if (result.type === "run-completed") {
                await finishRun(targetSessionId);
              } else {
                const cancelled = result.type === "run-cancelled";
                setState(cancelled ? "cancelled" : "failed");
                const serverMessage =
                  typeof event.message === "string" && event.message.trim()
                    ? event.message.trim()
                    : typeof event.content === "string" && event.content.trim()
                      ? event.content.trim()
                      : null;
                setError(
                  cancelled
                    ? "객체 중심 위험성평가를 취소했습니다."
                    : serverMessage ??
                        "객체 중심 위험성평가 생성 중 오류가 발생했습니다.",
                );
              }
              break;
            }
            if (terminal) break;
          }

          if (!terminal && !controller.signal.aborted) await sleep(700);
        } catch (caught) {
          if (
            controller.signal.aborted ||
            activeSessionIdRef.current !== targetSessionId
          ) {
            break;
          }
          if (caught instanceof Error && /401|403/.test(caught.message)) {
            setState("failed");
            setError("세션 인증을 확인할 수 없습니다.");
            break;
          }
          await sleep(900);
        }
      }

      if (streamAbortRef.current === controller) streamAbortRef.current = null;
    },
    [applyEvent, finishRun],
  );

  const loadSession = useCallback(
    async (targetSessionId: string) => {
      if (!isPublicSessionId(targetSessionId)) {
        setState("failed");
        setError("유효하지 않은 세션 주소입니다.");
        return;
      }
      streamAbortRef.current?.abort();
      activeSessionIdRef.current = targetSessionId;
      setSessionId(targetSessionId);
      setState("loading");
      setError(null);
      setArtifact(null);
      setStages(INITIAL_STAGES);
      contentRef.current = "";
      setStreamedContent("");

      try {
        const session = await loadAgentSession(targetSessionId);
        if (session.agent_id !== "object-risk-assessment") {
          throw new Error("객체 중심 위험성평가 세션이 아닙니다.");
        }
        const messages = await loadAgentSessionMessages(targetSessionId);
        if (activeSessionIdRef.current !== targetSessionId) return;

        const restoredFocus = focusTargetFromMessages(messages);
        if (!restoredFocus) {
          throw new Error("저장된 분석 대상 정보를 찾지 못했습니다.");
        }
        setFocusTarget(restoredFocus);

        const existing = [...extractRiskWorkspaceArtifacts(messages)]
          .reverse()
          .find((item) => item.type === "risk_assessment");
        if (existing) {
          setArtifact(existing);
          setStages(
            INITIAL_STAGES.map((stage) => ({ ...stage, status: "complete" })),
          );
          setState("ready");
          return;
        }

        const generating = latestGeneratingObjectRiskRun(messages);
        if (generating) {
          setRunId(generating.runId);
          setStages(
            INITIAL_STAGES.map((stage, index) => ({
              ...stage,
              status:
                index === 0
                  ? "complete"
                  : index === 1
                    ? "running"
                    : "pending",
            })),
          );
          setState("generating");
          void consumeRun(generating.runId, targetSessionId, generating.after);
          return;
        }

        throw new Error(
          "이 세션에서 완성된 객체 중심 위험성평가 보고서를 찾지 못했습니다.",
        );
      } catch (caught) {
        if (activeSessionIdRef.current !== targetSessionId) return;
        setState("failed");
        setError(
          caught instanceof Error
            ? caught.message
            : "세션을 불러오지 못했습니다.",
        );
      }
    },
    [consumeRun],
  );

  const start = useCallback(
    async (input: {
      originalFile: File;
      focusDraft: ObjectRiskFocusDraft;
      modelMode: AgentModelMode;
    }) => {
      if (["uploading", "generating"].includes(state)) return;

      streamAbortRef.current?.abort();
      setState("uploading");
      setError(null);
      setArtifact(null);
      setUploadProgress({
        loaded: 0,
        total: input.originalFile.size + input.focusDraft.file.size,
        percent: 0,
        originalPercent: 0,
        focusPercent: 0,
      });
      setStages(
        INITIAL_STAGES.map((stage, index) => ({
          ...stage,
          status: index === 0 ? "running" : "pending",
        })),
      );

      const controller = new AbortController();
      streamAbortRef.current = controller;
      let createdSessionId: string | null = null;

      try {
        const uploaded = await uploadObjectRiskPair({
          originalFile: input.originalFile,
          focusDraft: input.focusDraft,
          signal: controller.signal,
          onProgress: setUploadProgress,
        });
        setFocusTarget(uploaded.focusTarget);
        updateStage(0, "complete");

        createdSessionId = await createAgentSession({
          agentId: "object-risk-assessment",
          title: `${input.focusDraft.label || "선택 객체"} 위험성평가`,
          surface: "risk_assessment_workspace",
        });
        activeSessionIdRef.current = createdSessionId;
        setSessionId(createdSessionId);

        const deviceContext = await getDeviceContextSnapshot();
        const focusName =
          input.focusDraft.label ||
          input.focusDraft.textPrompt ||
          "사용자가 선택한 객체";
        const payload = buildAgentRunPayload({
          message: [
            `현장 사진에서 사용자가 선택한 “${focusName}” 객체를 분석 중심으로 위험성평가를 실시해주세요.`,
            "전체 현장 이미지는 객체의 배치·사용·주변 작업자·장비·구조물과의 상호작용을 이해하는 맥락으로 사용해주세요.",
            "선택 객체와 직접적 또는 작업상 의미 있는 인과관계가 없는 주변 일반 위험은 핵심 위험 항목에서 제외해주세요.",
          ].join("\n"),
          sessionId: createdSessionId,
          attachments: uploaded.attachments,
          focusTarget: uploaded.focusTarget,
          userLanguage: navigator.language || "ko-KR",
          modelMode: input.modelMode,
          reasoningStyle,
          deviceContext,
        });

        const createdRunId = await createAgentRun(
          "object-risk-assessment",
          payload,
          controller.signal,
        );
        setRunId(createdRunId);
        lastSequenceRef.current = 0;
        contentRef.current = "";
        setStreamedContent("");
        setState("generating");
        updateStage(1, "running");

        window.history.replaceState(
          null,
          "",
          getAgentSessionHref("object-risk-assessment", createdSessionId),
        );
        window.dispatchEvent(new Event("session-created"));
        void consumeRun(createdRunId, createdSessionId, 0);
      } catch (caught) {
        if (controller.signal.aborted) {
          setState("cancelled");
          setError("객체 중심 위험성평가를 취소했습니다.");
          return;
        }
        setState("failed");
        setError(
          caught instanceof Error
            ? caught.message
            : "객체 중심 위험성평가를 시작하지 못했습니다.",
        );
        if (!createdSessionId) activeSessionIdRef.current = null;
        updateStage(0, "error");
      }
    },
    [consumeRun, reasoningStyle, state, updateStage],
  );

  const cancel = useCallback(async () => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    if (runId) await cancelAgentRun(runId).catch(() => undefined);
    setState("cancelled");
    setError("객체 중심 위험성평가를 취소했습니다.");
  }, [runId]);

  useEffect(() => {
    if (!routeSessionId) return;
    if (!isPublicSessionId(routeSessionId)) {
      setState("failed");
      setError("유효하지 않은 세션 주소입니다.");
      return;
    }
    if (activeSessionIdRef.current === routeSessionId && state !== "loading") {
      return;
    }
    void loadSession(routeSessionId);
  }, [loadSession, routeSessionId, state]);

  useEffect(
    () => () => {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
    },
    [],
  );

  return {
    state,
    sessionId,
    runId,
    focusTarget,
    artifact,
    streamedContent,
    error,
    uploadProgress,
    stages,
    progressPercent,
    start,
    cancel,
    loadSession,
  };
}
